
import Vue from 'vue';

/**
 * Theme Provider Component.
 *
 * List of themeable component.
 *
 * Can see the variable [here](./theme.txt).
 *
 * All variables in `--kebab-case` become `camelCase`.<br />
 * Example: `colorPrimary` → `--color-primary`
 *
 * #### Base
 * - `colorPrimary`: Primary Color
 * - `colorSecondary`: Secondary Color
 * - `colorDestructive`: Destructive Color
 * - `colorDisabled`: Disabled Color
 * - `colorDefault`: Default button Color
 *
 * #### Button
 * - `colorButtonBackground`: Button Background color
 * - `colorButtonBorder`: Button Border color
 * - `colorButtonLabel`: Button Label color
 * - `colorButtonDisabled`: Button Disabled color
 * - `colorButtonDisabledText`: Button Disabled Text color
 *
 * #### Card
 * - `colorCardBackground`: Card Background
 * - `radiusCard`: Card Border radius
 *
 * #### Checkbox
 * - `colorCheckboxBackground`: Checkbox Background color
 * - `colorCheckboxBorder`: Checkbox Border color
 * - `colorCheckboxBackgroundDisabled`: Checkbox Background Disabled background color
 * - `colorCheckboxDisabled`: Checkbox Disabled background color
 * - `colorCheckboxIcon`: Checkbox Icon background color
 * - `radiusCheckbox`: Checkbox border radius
 *
 * * #### Collapsible
 * - `colorCollapsibleBorder`: Collapsible border color
 * - `colorCollapsibleBorderSelected`: Collapsible border color when selected
 * - `colorCollapsibleBackground`: Collapsible Background color
 * - `colorCollapsibleTitle`: Collapsible Title color
 * - `colorCollapsibleSubtitle`: Collapsible Subtitle color
 * - `colorCollapsibleDescription`: Collapsible Description color
 * - `colorCollapsibleBodyBg`: Collapsible expanded section background
 * - `colorCollapsibleBodyText`: Collapsible expanded section text color
 * - `colorCollapsibleBodyBorder`: Collapsible expanded body section border top color
 *
 * #### Contacts
 * - `colorContactsBackdrop`: The Contact backdrop
 * - `colorContactsLabel`: The Contact label
 * - `colorContactsGrid`: The Contact Grid Border color
 * - `colorContactsSublabel`: The Contact sublabel
 * - `colorContactsSubtitle`: The Contact Subtitle (only at grid state)
 * - `colorContactsMenuTitle`: The Contact menu title
 *
 * #### Datepicker
 * - `colorDatepickerPlaceholder`: Placeholder inside Datepicker input base
 * - `colorDatepickerDisabled`: Input color when Datepicker on disabled mode
 * - `colorDatepickerBorder`: Input Border color on Datepicker
 * - `colorDatepickerBackground`: Input Background color on Datepicker
 *
 * * #### DateTimePicker
 * - `colorDatetimeBackdrop`: Datetime Backdrop color
 * - `colorDatetimeSelected`: Datetime Selected background color
 * - `colorDatetimeSelectedColor`: Datetime SelectedColor text color
 * - `colorDatetimeHeaderBg`: Datetime Header background
 * - `colorDatetimeHeaderBorder`: Datetime Header border color
 * - `colorDatetimeHeaderText`: Datetime Header Text color
 * - `colorDatetimePlaceholder`: Datetime Placeholder color
 * - `colorDatetimeDisabled`: Datetime Disabled background color
 * - `colorDatetimeBorder`: Datetime Selected Date Border color
 * - `colorDatetimeBackground`: Datetime Selected Date Background color
 * - `colorDatetimeBackgroundHover`: Datetime Input Background Hover color
 * - `colorDatetimeBorderHover`: Datetime Selected Date Border Hover color
 * - `colorDatetimeButtonBg`: Datetime Button Background Color
 * - `colorDatetimeButtonBgHover`: Datetime Button Background Hover Color
 * - `colorDatetimeHintBg`: Datetime Timepicker Hint Background Color
 * - `colorDatetimeTimepickerBg`: Datetime Timepicker Background Color
 *
 * #### Dropdown
 * - `colorDropdownBackground`: Dropdown background color
 * - `colorDropdownBorder`: Dropdown border color
 * - `colorDropdownContentText`: Dropdown Content Text color
 * - `colorDropdownMenuBackground`: Dropdown Menu Background color
 * - `colorDropdownMenuBorder`: Dropdown Menu Border color
 * - `colorDropdownMenuHover`: Dropdown Menu Background color when on hover
 * - `colorDropdownDisabled`: Dropdown Background color when on disabled
 * - `colorDropdownPlaceholder`: Dropdown Placeholder color
 * - `colorDropdownScroll`: Dropdown Scrollbar color
 *
 * #### Expander
 * - `colorExpanderText`: Expander text color
 *
 * #### Onboarding Tooltips
 * - `colorOnboardingBackground`: Onboarding Background color
 * - `colorOnboardingBorder`: Onboarding Border color
 * - `colorOnboardingBackdrop`: Onboarding Backdrop color
 * - `colorOnboardingTooltips`: Onboarding Tooltips color
 * - `radiusOnboardingBorder`: Onboarding Button border radius
 *
 * #### General Action Card
 * - `colorGcardactionBackground`: GeneralActionCard Background color
 * - `colorGcardactionSubtitle`: GeneralActionCard Subtitle text color
 * - `colorGcardactionTitle`: GeneralActionCard Title text color
 * - `colorGcardactionIconBackground`: GeneralActionCard Background Icon color
 * - `colorGcardactionCtaBackground`: GeneralActionCard Button Background color on square variant (in gradient only)
 * - `colorGcardactionButtonBackground`: GeneralActionCard Button Background color on line variant
 * - `colorGcardactionButtonDisabled`: GeneralActionCard Button Disabled color
 *
 * #### General Add Card
 * - `colorGcardaddBackground`: GeneralAddCard Background color
 * - `colorGcardaddTitle`: GeneralAddCard Title text color
 *
 * #### General Card
 * - `colorGcardBorder`: GeneralCard Border color
 * - `colorGcardBackground`: GeneralCard Background color
 * - `colorGcardIconBase`: GeneralCard Icon Base color
 * - `colorGcardSubtitle`: GeneralCard Subtitle text color
 * - `colorGcardTitle`: GeneralCard Title text color
 * - `colorGcardList`: GeneralCard List text color
 * - `colorGcardViewlabel`: GeneralCard View label text color
 * - `colorGcardBottomText`: GeneralCard Bottom text color
 * - `colorGcardBottomSubtext`: GeneralCard Bottom Subtext color
 * - `colorGcardBottomBorder`: GeneralCard Bottom Border color
 * - `colorGcardBottomAdditionLabel`: GeneralCard Bottom Addition Label color
 * - `colorGcardBottomAdditionInfo`: GeneralCard Bottom Addition Info color
 * - `colorGcardBottomAdditionValue`: GeneralCard Bottom Addition Value color
 * - `colorGcardBottomAdditionButton`: GeneralCard Bottom Addition Button color
 * - `colorGcardPrimaryBackground`: GeneralCard Primary button background color (in gradient only)
 * - `colorGcardSecondaryBackground`: GeneralCard Secondary button background color
 * - `colorGcardSecondaryDisabled`: GeneralCard Secondary button background color on disabled
 * - `colorGcardSecondaryShadow`: GeneralCard Secondary button shadow color
 *
 * #### General Description Card
 * - `colorGdesccardHeader`: GeneralDescriptionCard Header text color
 * - `colorGdesccardBackground`: GeneralDescriptionCard Background color
 * - `colorGdesccardBorder`: GeneralDescriptionCard Border color
 * - `colorGdesccardImagebase`: GeneralDescriptionCard Image base background color
 * - `colorGdesccardList`: GeneralDescriptionCard list text color
 * - `colorGdesccardTitle`: GeneralDescriptionCard Title text color
 * - `colorGdesccardContent`: GeneralDescriptionCard Content text color
 * - `colorGdesccardUptoLabel`: GeneralDescriptionCard Up To label text color
 * - `colorGdesccardUptoValue`: GeneralDescriptionCard Up To Value text color
 * - `colorGdesccardButton`: GeneralDescriptionCard Button text color
 * - `colorGdesccardButtonBackground`: GeneralDescriptionCard Button Background color
 *
 * #### General Info Card
 * - `colorGinfocardBackground`: GeneralInfoCard Background color
 * - `colorGinfocardTitle`: GeneralInfoCard Title text color
 * - `colorGinfocardSubtitle`: GeneralInfoCard Subtitle text color
 * - `colorGinfocardBorder`: GeneralInfoCard Border color
 * - `colorGinfocardTitle`: GeneralInfoCard Title text color
 * - `colorGinfocardSelected`: GeneralInfoCard on selected background color
 *
 * #### General Infographic Card
 * - `colorGinfographicTitle`: GeneralInfographicCard Title text color
 * - `colorGinfographicContent`: GeneralInfographicCard Content text color
 * - `colorGinfographicButton`: GeneralInfographicCard Button text color
 * - `colorGinfographicButtonBg`: GeneralInfographicCard Button background color
 * - `colorGinfographicSecondbutton`: GeneralInfographicCard Secondary Button text color
 * - `colorGinfographicSecondbuttonBg`: GeneralInfographicCard Secondary Button background color
 * - `colorGinfographicDisabled`: GeneralInfographicCard Button disabled background color
 * - `colorGinfographicDisabledText`: GeneralInfographicCard Button disabled text color
 *
 * #### General List Item Card
 * - `colorGListCardBorder`: GeneralListCard Border color
 * - `colorGListCardLabel`: GeneralListCard Label text color
 * - `colorGListCardValue`: GeneralListCard Value text color
 *
 * #### General More Items Card
 * - `colorGmorecardTitle`: GeneralMoreItems Title text color
 * - `colorGmorecardText`: GeneralMoreItems text color
 * - `colorGmorecardBackground`: GeneralMoreItems Background color
 * - `colorGmorecardCategory`: GeneralMoreItems Category text color
 * - `colorGmorecardItembg`: GeneralMoreItems Item background color
 * - `colorGmorecardItemTitle`: GeneralMoreItems Item title text color
 * - `colorGmorecardItemContent`: GeneralMoreItems Item Content text color
 * - `colorGmorecardExpandBg`: GeneralMoreItems Expand background color
 * - `colorGmorecardExpandBorder`: GeneralMoreItems Expand border color
 *
 * #### General Product Card
 * - `colorGprodcardHeadertitle`: GeneralProductCard header title color (left side)
 * - `colorGprodcardHeaderlabel`: GeneralProductCard header label color (right side)
 * - `colorGprodcardHeadervalue`: GeneralProductCard header value color (right side)
 * - `colorGprodcardTitle`: GeneralProductCard title color (inside body)
 * - `colorGprodcardDesctitle`: GeneralProductCard Desc title color
 * - `colorGprodcardDescvalue`: GeneralProductCard Desc value color
 * - `colorGprodcardFootertitle`: GeneralProductCard remaining bar title
 * - `colorGprodcardLoadertext`: GeneralProductCard remaining bar text
 * - `colorGprodcardRemainedlabel`: GeneralProductCard remained label text
 * - `colorGprodcardLoaderIndicator`: GeneralProductCard loader indicator background color
 * - `colorGprodcardLoaderContainer`: GeneralProductCard loader container background color
 * - `colorGprodcardButton`: GeneralProductCard button color (as in gradient)
 *
 * #### General Promo Card
 * - `colorGpromocardBorder`: GeneralPromoCard border color
 * - `colorGpromocardTitle`: GeneralPromoCard Title color
 * - `colorGpromocardButton`: GeneralPromoCard Button background color
 * - `colorGpromocardCirclemask`: GeneralPromoCard promo icon background color
 *
 * #### General Tick Card
 * - `colorGtickcardBorder`: GeneralTickCard border color
 * - `colorGtickcardBackground`: GeneralTickCard Background color
 * - `colorGtickcardTitle`: GeneralTickCard Title color
 * - `colorGtickcardLabel`: GeneralTickCard Label color
 * - `colorGtickcardExpandTitle`: GeneralTickCard Expand Title color
 * - `colorGtickcardBodyBg`: GeneralTickCard expanded section background
 * - `colorGtickcardBodyText`: GeneralTickCard expanded section text color
 *
 * #### Image Preview Card
 * - `colorImagepreviewBackground`: ImagePreviewCard background color
 * - `colorImagepreviewTitle`: ImagePreviewCard title color
 *
 * #### InputAdvanced
 * - `colorInputadvBorder`: InputAdvanced border color
 * - `colorInputadvBackground`: InputAdvanced background color
 * - `colorInputadvBackgroundDisabled`: InputAdvanced background color when on disabled
 * - `colorInputadvCustom`: InputAdvanced custom section text color (left/right)
 * - `colorInputadvOddPhonelist`: InputAdvanced Country code selection odd list background color (when on phone number format)
 *
 * #### InputField
 * - `colorInputfieldLabel`: Input field left label color
 * - `colorInputfieldSublabel`: Input field right label color
 *
 * #### InputFilter
 * - `colorInputfilterBorder`: InputFilter border color
 * - `colorInputfilterBackground`: InputFilter background color
 * - `colorInputfilterOuterBackground`: InputFilter Outer background color
 * - `colorInputfilterDisabledBackground`: InputFilter Disabled background color
 * - `colorInputfilterDisabledShadow`: InputFilter Disabled Shadow color
 * - `colorInputfilterContainerBorder`: InputFilter Container border color
 * - `colorInputfilterDropdownBackground`: InputFilter Dropdown background
 * - `colorInputfilterPlaceholder`: InputFilter Placeholder background
 *
 * #### Modaldate
 * - `colorModaldateBackdrop`: Modaldate Backdrop color
 * - `colorModaldateSelected`: Modaldate Selected background color
 * - `colorModaldateSelectedColor`: Modaldate SelectedColor text color
 * - `colorModaldateHeaderBg`: Modaldate Header background
 * - `colorModaldateHeaderBorder`: Modaldate Header border color
 * - `colorModaldateHeaderText`: Modaldate Header Text color
 * - `colorModaldatePlaceholder`: Modaldate Placeholder color
 * - `colorModaldateDisabled`: Modaldate Disabled background color
 * - `colorModaldateBorder`: Modaldate Border color
 * - `colorModaldateBackground`: Modaldate Input Background color
 *
 * #### Navbar
 * - `colorNavbarShadow`: Navbar shadow color
 *
 * #### Purchase Detail
 * - `colorPurchasedetailTitle`: Purchase Detail title color
 * - `colorPurchasedetailSubtitle`: Purchase Detail Subtitle color
 * - `colorPurchasedetailValue`: Purchase Detail Value color
 * - `colorPurchasedetailBorder`: Purchase Detail Border color
 * - `colorPurchasedetailButton`: Purchase Detail Button color (on gradient)
 * - `colorPurchasedetailDisabled`: Purchase Detail Disabled button color
 * - `colorPurchasedetailDisabledText`: Purchase Detail Disabled Button Text color
 *
 * #### Radio
 * - `colorRadioDisabled`: Radio disabled color
 * - `colorRadioSelectedBorder`: Radio Selected Border color
 * - `colorRadioInnerBorder`: Radio Inner Border color
 * - `colorRadioBackground`: Radio background color
 * - `colorRadioInnerBackground`: Radio inner section background color
 * - `colorRadioInnerBackgroundChecked`: Radio inner section background color when checked
 *
 * #### Range Slider
 * - `colorRangesliderBackground`: RangeSlider background color
 * - `colorRangesliderShadow`: RangeSlider Shadow color
 * - `colorRangesliderTrackBackground`: RangeSlider Track Background color
 * - `colorRangesliderSliderBorder`: RangeSlider Slider border
 * - `colorRangesliderSliderBackground`: RangeSlider Slider Background
 * - `colorRangesliderRange`: RangeSlider Slider Range color
 *
 * #### Remaining Bar
 * - `colorRemainingbarTitle`: Remainingbar Title color
 * - `colorRemainingbarText`: Remainingbar Text color
 * - `colorRemainingbarRemainLabel`: Remainingbar Remain Label color
 * - `colorRemainingbarContainerBackground`: Remainingbar Container Background
 * - `colorRemainingbarIndicatorBackground`: Remainingbar Indicator Background
 *
 * #### Sliderdate
 * - `colorSliderdateLabel`: Sliderdate label color
 * - `colorSliderdateBorder`: Sliderdate Border color
 * - `colorSliderdateBackground`: Sliderdate Background color
 * - `colorSliderdateDisabled`: Sliderdate Disabled color
 * - `colorSliderdateButton`: Sliderdate Button background color (as in gradient)
 * - `colorSliderdateListborder`: Sliderdate List border color
 * - `colorSliderdateList`: Sliderdate List background color
 * - `colorSliderdateListActive`: Sliderdate List background color when on active
 *
 * #### Statusbar
 * - `colorStatusbarBackground`: Statusbar background color
 * - `colorStatusbarBorder`: Statusbar Border color (align with the background color)
 * - `colorStatusbarActive`: Statusbar background color when on active
 * - `colorStatusbarLineBackground`: Statusbar line background color when not on active
 * - `colorStatusbarLabel`: Statusbar label color
 *
 * #### Tabs
 * - `colorTabBackgroundButton`: Tabs button background color
 * - `colorTabBackgroundCircular`: Tabs Circular background
 * - `colorTabBackgroundActive`: Tabs Active background
 * - `colorTabBackgroundInactive`: Tabs Inactive background
 * - `colorTabBorderButton`: Tabs Border button color
 * - `colorTabText`: Tabs text color
 * - `colorTabTextActive`: Tabs text color when active
 * - `colorTabTextNonSkeleton`: Tabs NonSkeleton text color
 * - `colorTabTextActiveNonSkeleton`: Tabs NonSkeleton text color when on active
 *
 * #### Tag Container
 * - `colorTagconBackground`: Tag Container Background color
 * - `colorTagconBorder`: Tag Container Border
 * - `colorTagconText`: Tag Container Text
 *
 * #### Tooltips
 * - `colorTooltipsBackground`: Tooltips Background color
 * - `colorTooltipsText`: Tooltips Text color
 * - `colorTooltipsBorder`: Tooltips Border color
 *
 * #### Timepicker
 * - `colorTimepickerDisabledText`: Timepicker disabled text color
 * - `colorTimepickerDisabledBackground`: Timepicker disabled Background color
 * - `colorTimepickerPlaceholder`: Timepicker Placeholder color
 * - `colorTimepickerBorder`: Timepicker Border color
 * - `colorTimepickerBackground`: Timepicker Background color
 */
export default Vue.extend<any, any, any, any>({
  name: 'ThemeProvider',
  props: {
    /**
     * Theme Object, described on the top.
     */
    theme: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    variableCss() {
      let result = '';
      Object.keys(this.theme).forEach(themeKey => {
        result += `--${this.toKebabCase(themeKey)}: ${this.theme[themeKey]};`;
      });
      return result;
    }
  },
  methods: {
    toKebabCase(str: string) {
      return str
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
        .replace(/([A-Z])([A-Z])(?=[a-z])/g, '$1-$2')
        .toLowerCase();
    }
  },
  render(h: Vue.CreateElement) {
    return h('div', { style: this.variableCss }, [
      /**
       * @slot Content of your apps.
       */
      this.$slots.default
    ]);
  }
});
